import { EntryLink, useInertiaEntry } from '@/components/entry-link';
import { useLocalizedRoute } from '@/hooks/use-localized-route';
import { needsDocumentNavigation } from '@/lib/app-entry';
import { type SharedData } from '@/types';
import { router, usePage } from '@inertiajs/react';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsToggle from '../general/settings-toggle';
import AppLogoIcon from '../icon/app-logo-icon';

export default function Header() {
    const { t } = useTranslation();
    const { auth } = usePage<SharedData>().props;
    const route = useLocalizedRoute();
    const entry = useInertiaEntry();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navigation = [
        { name: t('navigation.features'), href: '#features' },
        { name: t('navigation.howItWorks'), href: '#how-it-works' },
        { name: t('navigation.faq'), href: '#faq' },
    ];

    const handleNavigation = (href: string) => {
        const homeUrl = route('home');
        const isHomePage = window.location.pathname === homeUrl || window.location.pathname === homeUrl + '/';
        const anchor = href.substring(1);

        if (isHomePage) {
            const element = document.getElementById(anchor);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        } else if (needsDocumentNavigation(homeUrl, entry)) {
            window.location.assign(`${homeUrl}${href}`);
        } else {
            router.visit(homeUrl, {
                onSuccess: () => {
                    requestAnimationFrame(() => {
                        const element = document.getElementById(anchor);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                        }
                    });
                },
            });
        }
    };

    return (
        <header className="bg-white dark:bg-gray-950">
            <nav aria-label="Global" className="mx-auto flex w-full items-center justify-between p-6 lg:px-8">
                <div className="flex flex-1">
                    <div className="hidden lg:flex lg:gap-x-12">
                        {navigation.map((item) => (
                            <button
                                key={item.name}
                                onClick={() => handleNavigation(item.href)}
                                className="text-md/6 cursor-pointer font-semibold text-headline hover:text-hs-blue dark:hover:text-hs-green-light"
                            >
                                {item.name}
                            </button>
                        ))}
                    </div>
                    <div className="flex lg:hidden">
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(true)}
                            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-headline"
                        >
                            <span className="sr-only">{t('home.header.openMenu')}</span>
                            <Menu aria-hidden="true" className="size-6" />
                        </button>
                    </div>
                </div>

                <div className="flex">
                    <EntryLink href={route('home')} className="-m-1.5 p-1.5">
                        <span className="sr-only">{t('common.appName')}</span>
                        <AppLogoIcon className="h-10 w-auto" />
                    </EntryLink>
                </div>

                <div className="flex flex-1 items-center justify-end gap-x-8">
                    {auth?.user ? (
                        <EntryLink
                            href={route('dashboard')}
                            className="text-md/6 hidden font-semibold text-headline hover:text-hs-blue lg:block dark:hover:text-hs-green-light"
                        >
                            {t('navigation.dashboard')}
                        </EntryLink>
                    ) : (
                        <>
                            <EntryLink
                                href={route('login')}
                                className="text-md/6 hidden font-semibold text-headline hover:text-hs-blue lg:block dark:hover:text-hs-green-light"
                            >
                                {t('home.header.login')}
                            </EntryLink>
                            <EntryLink
                                href={route('register')}
                                className="text-md/6 hidden font-semibold text-headline hover:text-hs-blue lg:block dark:hover:text-hs-green-light"
                            >
                                {t('home.header.register')}
                            </EntryLink>
                        </>
                    )}
                    <div className="hidden sm:block">
                        <SettingsToggle />
                    </div>
                </div>
            </nav>

            {/* Mobile menu */}
            {mobileMenuOpen && (
                <div className="lg:hidden">
                    <div className="fixed inset-0 z-50"></div>
                    <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10 dark:bg-gray-900">
                        <div className="flex items-center justify-between">
                            <EntryLink href={route('home')} className="-m-1.5 p-1.5">
                                <span className="sr-only">{t('common.appName')}</span>
                                <AppLogoIcon className="h-8 w-auto" />
                            </EntryLink>
                            <button type="button" onClick={() => setMobileMenuOpen(false)} className="-m-2.5 rounded-md p-2.5 text-headline">
                                <span className="sr-only">{t('home.header.closeMenu')}</span>
                                <X aria-hidden="true" className="size-6" />
                            </button>
                        </div>
                        <div className="mt-6 flow-root">
                            <div className="-my-6 divide-y divide-gray-500/10">
                                <div className="space-y-2 py-6">
                                    {navigation.map((item) => (
                                        <button
                                            key={item.name}
                                            onClick={() => {
                                                handleNavigation(item.href);
                                                setMobileMenuOpen(false);
                                            }}
                                            className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-headline hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            {item.name}
                                        </button>
                                    ))}
                                </div>
                                <div className="py-6">
                                    {auth?.user ? (
                                        <EntryLink
                                            href={route('dashboard')}
                                            className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-headline hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            {t('navigation.dashboard')}
                                        </EntryLink>
                                    ) : (
                                        <>
                                            <EntryLink
                                                href={route('login')}
                                                className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-headline hover:bg-gray-50 dark:hover:bg-gray-800"
                                            >
                                                {t('home.header.login')}
                                            </EntryLink>
                                            <EntryLink
                                                href={route('register')}
                                                className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-headline hover:bg-gray-50 dark:hover:bg-gray-800"
                                            >
                                                {t('home.header.register')}
                                            </EntryLink>
                                        </>
                                    )}
                                    <div className="mt-4 flex justify-start">
                                        <SettingsToggle />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
